/**
 * @module types
 * @description ChartSettingsV2 公共类型定义
 *
 * 关键：
 * - 本文件：面板状态机（② 存储契约）— IChartSettingsState / Action / QBStateSnapshot
 * - 配置项字段形状（① 领域模型）：`types/settings/` — ISettingsStore 等
 * - config/context/services 从本文件或 types/settings 按职责导入
 *
 * @see 状态管理方案 §二 — 状态模型
 * @see 渲染方案 §三 — visibility-factories
 */

import type { TChartType } from '@dbl/core/chart';
import type { ISettingsStore } from './settings';

/**
 * base / draft 单层存储类型：已知配置项字段 + 兼容未类型化 key
 *
 * 叠加 `Record<string, unknown>` 以便存量 `as` 与未登记字段逐步迁移，不一次性引爆编译错误。
 */
export type TChartSettingsConfigLayer = ISettingsStore & Record<string, unknown>;

// ── QB 状态快照（来自 QueryContext）──

/**
 * QB（查询构建器）状态的快照，用于计算配置项显隐条件
 *
 * 仅包含影响配置项显隐的字段，不包含全部 QB 状态
 * 由 Panel 层从 QueryContext 读取后构造
 */
export interface QBStateSnapshot {
  /** 当前图表类型 */
  chartType: TChartType;
  /** 维度列表 */
  columnDimensions: unknown[];
  /** 指标列表 */
  columnMetrics: unknown[];
  /** 是否有日期维度 */
  hasDateDimension: boolean;
  /** 计算模式：aggregation=聚合, raw=原始数据 */
  mode: 'aggregation' | 'raw';
  /** 排序方式（'' | 'defaultSort' | 'customMetricSort' | 'manual' | 'x-axis'） */
  sortType: string;
  /** 是否开启了双Y轴 */
  dualYAxisEnabled: boolean;
  /** 表格是否为交叉透视表（有维度被透视到列区） */
  isCrosstabActive: boolean;
}

// ── ChartSettingsContext 内部状态 ──

/**
 * ChartSettingsContext 内部状态
 *
 * base: 已确认的全局配置，不分图表类型，所有配置项扁平存储
 * draft: 编辑中的草稿，用户修改但未 Confirm 的值
 * hasDraft: 逐配置项标记未确认状态，UI 显示 ⬤ 指示
 */
export interface IChartSettingsState {
  /** 面板打开/关闭 */
  chartsModal: boolean;
  /**
   * Confirm 触发重算的版本号 — 每次需要后端重算时 +1，Search 通过 useUpdateEffect 监听变化
   * 初始为 0；仅递增、不重置，避免 boolean 信号需二次 reset 才能重复触发
   */
  recalcTriggerVersion: number;
  /** 当前激活的 Tab ID */
  activeTabs: string;

  /** 已确认的全局配置 — base 不分图表类型 */
  base: TChartSettingsConfigLayer;
  /** 编辑中的全局草稿 — draft 优先于 base（draft.xxx ?? base.xxx） */
  draft: TChartSettingsConfigLayer;
  /** 配置项级别未确认标记 — hasDraft[key]=true 表示该配置项有未确认修改 */
  hasDraft: Record<string, boolean>;
}

// ── Action 体系 ──

/**
 * ChartSettingsContext Action 枚举
 *
 * 与 V2 QueryContext.QActionType 风格一致
 */
export enum CSActionType {
  /** 打开/关闭面板 */
  SET_MODAL = 'CS_SET_MODAL',
  /** Confirm 后递增重算触发版本号（仅 needsRecalculation 时 dispatch） */
  BUMP_RECALC_TRIGGER = 'CS_BUMP_RECALC_TRIGGER',
  /** 切换 Tab */
  SET_ACTIVE_TAB = 'CS_SET_ACTIVE_TAB',
  /** 写入全局 BASE — 浅合并（单字段补丁、列排序等） */
  SET_BASE = 'CS_SET_BASE',
  /** 进页 / QB 重置 / chartType 变化 — 整表替换 BASE，避免跨图表类型 key 残留 */
  SET_BASE_REPLACE = 'CS_SET_BASE_REPLACE',
  /** 更新 Draft + 逐项标记 hasDraft */
  UPDATE_DRAFT = 'CS_UPDATE_DRAFT',
  /** Draft → Base 合并 + 清空 Draft/hasDraft */
  COMMIT_DRAFT = 'CS_COMMIT_DRAFT',
  /** 丢弃 Draft + 清空 hasDraft */
  RESET_DRAFT = 'CS_RESET_DRAFT',
  /** 批量更新多个字段 */
  PATCH = 'CS_PATCH',
  /** 恢复初始状态 */
  RESET = 'CS_RESET',
  /** QB 联动管道合并写入 base + draft（单次 dispatch） */
  MERGE_QB_PATCH = 'CS_MERGE_QB_PATCH',
}

/**
 * Action 联合类型 — 使用显式对象类型避免泛型条件类型导致的类型窄化失败
 *
 * 设计决策：不使用泛型 Action<T,P> 帮助类型，因为 P extends void 条件类型
 * 会导致 TS 无法正确窄化联合类型，迫使调用方使用 as never 绕过检查。
 * 显式定义每个成员让 TS 基于 type 字段进行可辨识联合窄化。
 */
export type TCSAction =
  | { type: CSActionType.SET_MODAL; payload: boolean }
  | { type: CSActionType.BUMP_RECALC_TRIGGER }
  | { type: CSActionType.SET_ACTIVE_TAB; payload: string }
  | { type: CSActionType.SET_BASE; payload: TChartSettingsConfigLayer }
  | { type: CSActionType.SET_BASE_REPLACE; payload: TChartSettingsConfigLayer }
  | { type: CSActionType.UPDATE_DRAFT; payload: TChartSettingsConfigLayer }
  | { type: CSActionType.COMMIT_DRAFT }
  | { type: CSActionType.RESET_DRAFT }
  | { type: CSActionType.PATCH; payload: Partial<IChartSettingsState> }
  | { type: CSActionType.RESET }
  | {
      type: CSActionType.MERGE_QB_PATCH;
      payload: {
        base?: TChartSettingsConfigLayer;
        draft?: TChartSettingsConfigLayer;
      };
    };

// ── 初始状态 ──

/** ChartSettingsContext 初始状态 */
export const INITIAL_STATE: IChartSettingsState = {
  chartsModal: false,
  recalcTriggerVersion: 0,
  activeTabs: 'basic',
  base: {},
  draft: {},
  hasDraft: {},
};
